import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { getDashboardStats } from '../api/statsApi';
import { COLORS } from '../theme/theme';

type StatusMetric = {
    bols: number;
    amount: number;
    skids: number;
};

type StatusStats = {
    pending: StatusMetric;
    unapproved: StatusMetric;
    approvedPendingPayment: StatusMetric;
    paid: StatusMetric;
    total: StatusMetric;
};

type YearlySpendStat = {
    year: number;
    totalBols: number;
    totalAmount: number;
    totalSkids: number;
    paidBols: number;
    paidAmount: number;
    paidSkids: number;
    unpaidBols: number;
    unpaidAmount: number;
    unpaidSkids: number;
};

type MonthlySpendStat = {
    year: number;
    month: string;
    monthNumber: number;
    totalBols: number;
    totalAmount: number;
    totalSkids: number;
};

type TopSpendStat = {
    name: string;
    totalBols: number;
    totalAmount: number;
    totalSkids: number;
};

type TrailerSpendStat = {
    trailer: string;
    totalBols: number;
    totalAmount: number;
    totalSkids: number;
};

type StatsResponse = {
    filters: {
        startDate: string;
        endDate: string;
        year: number;
        previousYear: number;
        company: string | null;
        topSuppliersLimit: number;
        topReceivingLocationsLimit: number;
    };
    statusStats: StatusStats;
    yearlySpend: YearlySpendStat[];
    monthlySpendCurrentVsLastYear: MonthlySpendStat[];
    topSuppliersBySpend: TopSpendStat[];
    topReceivingLocationsBySpend: TopSpendStat[];
    trailerSpend: TrailerSpendStat[];
    shuntingStats: ShuntingStats;
};

type ShuntingStats = {
    totalJobs: number;
    paidJobs: number;
    unpaidJobs: number;
    paidAmount: number;
    unpaidAmount: number;
    company_name: string | null;
};
type StatCardItem = {
    title: string;
    count: number;
    amount: number;
    skids: number;
    status: string;
};

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const defaultStatusStats: StatusStats = {
    pending: {
        bols: 0,
        amount: 0,
        skids: 0,
    },
    unapproved: {
        bols: 0,
        amount: 0,
        skids: 0,
    },
    approvedPendingPayment: {
        bols: 0,
        amount: 0,
        skids: 0,
    },
    paid: {
        bols: 0,
        amount: 0,
        skids: 0,
    },
    total: {
        bols: 0,
        amount: 0,
        skids: 0,
    },
};

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        maximumFractionDigits: 2,
    }).format(Number(value || 0));

const formatNumber = (value: number) =>
    new Intl.NumberFormat('en-CA').format(Number(value || 0));

const formatCompactCurrency = (value: number) => {
    const amount = Number(value || 0);

    if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
    if (amount >= 1_000) return `$${Math.round(amount / 1_000)}K`;

    return `$${amount.toFixed(0)}`;
};

const getPercent = (value: number, total: number) => {
    if (!total) return '0.0';
    return ((Number(value || 0) / Number(total || 0)) * 100).toFixed(1);
};

const statCards = (statusStats: StatusStats): StatCardItem[] => [
    {
        title: 'Pending',
        count: statusStats.pending.bols,
        amount: statusStats.pending.amount,
        skids: statusStats.pending.skids,
        status: 'warning',
    },
    {
        title: 'Unapproved',
        count: statusStats.unapproved.bols,
        amount: statusStats.unapproved.amount,
        skids: statusStats.unapproved.skids,
        status: 'error',
    },
    {
        title: 'Approved / Pending Payment',
        count: statusStats.approvedPendingPayment.bols,
        amount: statusStats.approvedPendingPayment.amount,
        skids: statusStats.approvedPendingPayment.skids,
        status: 'info',
    },
    {
        title: 'Paid',
        count: statusStats.paid.bols,
        amount: statusStats.paid.amount,
        skids: statusStats.paid.skids,
        status: 'success',
    },
    {
        title: 'Total',
        count: statusStats.total.bols,
        amount: statusStats.total.amount,
        skids: statusStats.total.skids,
        status: 'primary',
    },
];

function MonthlySpendTooltip({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: any[];
    label?: string;
}) {
    if (!active || !payload?.length) return null;

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1.5,
                boxShadow: 3,
                minWidth: 220,
            }}
        >
            <Typography variant="body2" fontWeight={800} sx={{ mb: 1 }}>
                {label}
            </Typography>

            <Stack spacing={1}>
                {payload.map((item) => {
                    const year = String(item.dataKey).replace('Amount', '');
                    const row = item.payload;

                    return (
                        <Box key={item.dataKey}>
                            <Typography
                                variant="body2"
                                fontWeight={800}
                                sx={{ color: item.color }}
                            >
                                {year}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Amount: {formatCurrency(Number(item.value || 0))}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                BOLs: {formatNumber(Number(row[`${year}Bols`] || 0))}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Skids: {formatNumber(Number(row[`${year}Skids`] || 0))}
                            </Typography>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

export default function DashboardPage() {
    const { user } = useSelector((state: any) => state.user);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [stats, setStats] = useState<StatsResponse | null>(null);

    const now = new Date();

    const firstDay = new Date(now.getFullYear(), 0, 1)
        .toISOString()
        .split('T')[0];

    const lastDay = new Date(now.getFullYear(), 11, 31)
        .toISOString()
        .split('T')[0];

    const [filters, setFilters] = useState({
        startDate: firstDay,
        endDate: lastDay,
        year: now.getFullYear(),
        company: 'G2000',
    });

    const fetchStats = async (customFilters = filters) => {
        try {
            setLoading(true);
            setError('');

            const payload: Record<string, any> = {
                year: customFilters.year,
            };

            if (customFilters.startDate) payload.startDate = customFilters.startDate;
            if (customFilters.endDate) payload.endDate = customFilters.endDate;
            if (customFilters.company?.trim()) payload.company = customFilters.company.trim();

            const data = await getDashboardStats(payload);
            setStats(data as any);
        } catch (err) {
            console.error('Error fetching stats:', err);
            setError('Error fetching dashboard stats.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const startDate = new Date(filters.year, 0, 1).toISOString().split('T')[0];
        const endDate = new Date(filters.year, 11, 31).toISOString().split('T')[0];

        setFilters((prev) => ({
            ...prev,
            startDate,
            endDate,
        }));
    }, [filters.year]);

    const statusStats = stats?.statusStats || defaultStatusStats;

    const yearlySpend = useMemo(
        () => stats?.yearlySpend || [],
        [stats?.yearlySpend]
    );

    const monthlySpendCurrentVsLastYear = useMemo(
        () => stats?.monthlySpendCurrentVsLastYear || [],
        [stats?.monthlySpendCurrentVsLastYear]
    );

    const topSuppliersBySpend = useMemo(
        () => stats?.topSuppliersBySpend || [],
        [stats?.topSuppliersBySpend]
    );

    const topReceivingLocationsBySpend = useMemo(
        () => stats?.topReceivingLocationsBySpend || [],
        [stats?.topReceivingLocationsBySpend]
    );

    const trailerSpend = useMemo(
        () => stats?.trailerSpend || [],
        [stats?.trailerSpend]
    );

    const cards = useMemo(() => statCards(statusStats), [statusStats]);

    const selectedYear = stats?.filters?.year || filters.year || currentYear;
    const previousYear = stats?.filters?.previousYear || selectedYear - 1;

    const monthlyYears = useMemo(
        () =>
            Array.from(new Set(monthlySpendCurrentVsLastYear.map((item) => item.year))).sort(
                (a, b) => a - b
            ),
        [monthlySpendCurrentVsLastYear]
    );

    const monthlyAmountChartData = useMemo(() => {
        return monthNames.map((month, index) => {
            const row: Record<string, any> = { month };

            monthlyYears.forEach((year) => {
                const match = monthlySpendCurrentVsLastYear.find(
                    (item) => item.year === year && item.monthNumber === index + 1
                );

                row[`${year}Amount`] = match?.totalAmount || 0;
                row[`${year}Bols`] = match?.totalBols || 0;
                row[`${year}Skids`] = match?.totalSkids || 0;
            });

            return row;
        });
    }, [monthlySpendCurrentVsLastYear, monthlyYears]);

    const monthlyBolsChartData = useMemo(() => {
        return monthNames.map((month, index) => {
            const row: Record<string, any> = { month };

            monthlyYears.forEach((year) => {
                const match = monthlySpendCurrentVsLastYear.find(
                    (item) => item.year === year && item.monthNumber === index + 1
                );

                row[`${year}Bols`] = match?.totalBols || 0;
                row[`${year}Skids`] = match?.totalSkids || 0;
            });

            return row;
        });
    }, [monthlySpendCurrentVsLastYear, monthlyYears]);

    const yearlySpendChartData = useMemo(
        () =>
            yearlySpend.map((item) => ({
                year: String(item.year),
                paidAmount: item.paidAmount,
                unpaidAmount: item.unpaidAmount,
                totalAmount: item.totalAmount,
                totalBols: item.totalBols,
                totalSkids: item.totalSkids,
            })),
        [yearlySpend]
    );

    const topSuppliersChartData = useMemo(
        () =>
            topSuppliersBySpend.map((item) => ({
                name: item.name,
                totalAmount: item.totalAmount,
                totalBols: item.totalBols,
                totalSkids: item.totalSkids,
            })),
        [topSuppliersBySpend]
    );

    const topLocationsChartData = useMemo(
        () =>
            topReceivingLocationsBySpend.map((item) => ({
                name: item.name,
                totalAmount: item.totalAmount,
                totalBols: item.totalBols,
                totalSkids: item.totalSkids,
            })),
        [topReceivingLocationsBySpend]
    );

    const trailerSpendChartData = useMemo(
        () =>
            trailerSpend.map((item) => ({
                trailer: item.trailer,
                totalAmount: item.totalAmount,
                totalBols: item.totalBols,
                totalSkids: item.totalSkids,
            })),
        [trailerSpend]
    );

    const totalMonthlyAmount = useMemo(
        () => monthlySpendCurrentVsLastYear.reduce((sum, item) => sum + item.totalAmount, 0),
        [monthlySpendCurrentVsLastYear]
    );
    const previousYearMonthlyAmount = useMemo(
        () =>
            monthlySpendCurrentVsLastYear
                .filter((item) => item.year === previousYear)
                .reduce((sum, item) => sum + item.totalAmount, 0),
        [monthlySpendCurrentVsLastYear, previousYear]
    );

    const selectedYearMonthlyAmount = useMemo(
        () =>
            monthlySpendCurrentVsLastYear
                .filter((item) => item.year === selectedYear)
                .reduce((sum, item) => sum + item.totalAmount, 0),
        [monthlySpendCurrentVsLastYear, selectedYear]
    );
    const totalMonthlyBols = useMemo(
        () => monthlySpendCurrentVsLastYear.reduce((sum, item) => sum + item.totalBols, 0),
        [monthlySpendCurrentVsLastYear]
    );

    const totalMonthlySkids = useMemo(
        () => monthlySpendCurrentVsLastYear.reduce((sum, item) => sum + item.totalSkids, 0),
        [monthlySpendCurrentVsLastYear]
    );

    const paymentRate = getPercent(statusStats.paid.bols, statusStats.total.bols);
    const pendingRate = getPercent(statusStats.pending.bols, statusStats.total.bols);
    const unapprovedRate = getPercent(statusStats.unapproved.bols, statusStats.total.bols);

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 2 } }}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Overview of BOL status, yearly spend, monthly spend, suppliers, receivers, and trailer spending<span style={{ color: 'red', fontWeight: 'bold' }}>(HST included)</span>.
                    </Typography>
                </Box>

                <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    select
                                    label="Year"
                                    value={filters.year}
                                    onChange={(e) =>
                                        setFilters((prev) => ({ ...prev, year: Number(e.target.value) }))
                                    }
                                >
                                    {years.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="company-label">Company</InputLabel>
                                    <Select
                                        labelId="company-label"
                                        label="Company"
                                        value={filters.company}
                                        disabled={user?.category === 'VENDOR'}
                                        onChange={(e) =>
                                            setFilters((prev) => ({ ...prev, company: e.target.value }))
                                        }
                                    >
                                        <MenuItem value="G2000">G2000</MenuItem>
                                        <MenuItem value="SVP-SPORTS">SVP-SPORTS</MenuItem>
                                        <MenuItem value="SMARTWAY">SMARTWAY</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="Start Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={filters.startDate}
                                    onChange={(e) =>
                                        setFilters((prev) => ({ ...prev, startDate: e.target.value }))
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="End Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={filters.endDate}
                                    onChange={(e) =>
                                        setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                    <Button
                                        variant="contained"
                                        sx={{ flexGrow: 1 }}
                                        onClick={() => fetchStats()}
                                        disabled={loading}
                                    >
                                        Apply
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        sx={{ flexGrow: 1 }}
                                        disabled={loading}
                                        onClick={() => {
                                            const resetFilters = {
                                                startDate: firstDay,
                                                endDate: lastDay,
                                                year: currentYear,
                                                company: '',
                                            };
                                            setFilters(resetFilters);
                                            fetchStats(resetFilters);
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {error ? <Alert severity="error">{error}</Alert> : null}

                {loading ? (
                    <Box
                        sx={{
                            minHeight: 240,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={2}>
                            {cards.map((card) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }} key={card.title}>
                                    <StatsSummaryCard
                                        title={card.title}
                                        count={card.count}
                                        amount={card.amount}
                                        skids={card.skids}
                                        status={card.status}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, lg: 12 }}>
                                <Card sx={{ borderRadius: 3, height: 430 }}>
                                    <CardContent sx={{ height: '100%' }}>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            Spend in {previousYear} ({formatCurrency(previousYearMonthlyAmount)}) vs {selectedYear} ({formatCurrency(selectedYearMonthlyAmount)})
                                        </Typography>

                                        <Box sx={{ width: '100%', height: 350 }}>
                                            <ResponsiveContainer>
                                                <BarChart data={monthlyAmountChartData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="month" />
                                                    <YAxis tickFormatter={(value) => formatCompactCurrency(Number(value || 0))} />
                                                    <Tooltip content={<MonthlySpendTooltip />} />
                                                    <Legend />

                                                    {monthlyYears.map((year, index) => (
                                                        <Bar
                                                            key={year}
                                                            dataKey={`${year}Amount`}
                                                            name={`${year} Amount`}
                                                            fill={index === 0 ? COLORS.brand.primary : COLORS.status.success}
                                                            radius={[8, 8, 0, 0]}
                                                        />
                                                    ))}
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* <Grid size={{ xs: 12, lg: 4 }}>
                                <Card sx={{ borderRadius: 3, height: 430 }}>
                                    <CardContent sx={{ height: '100%' }}>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            Monthly BOLs / Skids
                                        </Typography>

                                        <Box sx={{ width: '100%', height: 350 }}>
                                            <ResponsiveContainer>
                                                <BarChart data={monthlyBolsChartData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="month" />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Legend />

                                                    {monthlyYears.map((year, index) => (
                                                        <Bar
                                                            key={`${year}-bols`}
                                                            dataKey={`${year}Bols`}
                                                            name={`${year} BOLs`}
                                                            fill={index === 0 ? COLORS.brand.primary : COLORS.status.info}
                                                            radius={[8, 8, 0, 0]}
                                                        />
                                                    ))}
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid> */}
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, lg: 7 }}>
                                <Card sx={{ borderRadius: 3, height: 400 }}>
                                    <CardContent sx={{ height: '100%' }}>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            Yearly Spend: Paid vs Unpaid
                                        </Typography>

                                        <Box sx={{ width: '100%', height: 320 }}>
                                            <ResponsiveContainer>
                                                <BarChart data={yearlySpendChartData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="year" />
                                                    <YAxis tickFormatter={(value) => formatCompactCurrency(Number(value || 0))} />
                                                    <Tooltip formatter={(value) => formatCurrency(Number(value ?? 0))} />
                                                    <Legend />
                                                    <Bar
                                                        dataKey="paidAmount"
                                                        name="Paid"
                                                        stackId="a"
                                                        fill={COLORS.status.success}
                                                        radius={[8, 8, 0, 0]}
                                                    />
                                                    <Bar
                                                        dataKey="unpaidAmount"
                                                        name="Unpaid"
                                                        stackId="a"
                                                        fill={COLORS.status.warning}
                                                        radius={[8, 8, 0, 0]}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={{ xs: 12, lg: 5 }}>
                                <Card sx={{ borderRadius: 3, height: 400 }}>
                                    <CardContent sx={{ height: '100%' }}>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            Spending by company
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Amount / BOLs / Skids by trailer company
                                        </Typography>

                                        <Grid container spacing={2}>
                                            {trailerSpend.map((item) => (
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.trailer}>
                                                    <Card
                                                        sx={{
                                                            borderRadius: 2.5,
                                                            boxShadow: 'none',
                                                            border: `1px solid ${COLORS.neutral[200]}`,
                                                            height: '100%',
                                                        }}
                                                    >
                                                        <CardContent>
                                                            <Stack spacing={0.75}>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    fontWeight={700}
                                                                    noWrap
                                                                    title={item.trailer}
                                                                >
                                                                    {item.trailer}
                                                                </Typography>

                                                                <Typography variant="h6" fontWeight={800} color={COLORS.brand.primary}>
                                                                    {formatCurrency(item.totalAmount)}
                                                                </Typography>
                                                                <Stack direction="row" spacing={1}>
                                                                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                                                        {formatNumber(item.totalBols)} BOLs /
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                                                        {formatNumber(item.totalSkids)} Skids
                                                                    </Typography>
                                                                </Stack>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                            {stats?.shuntingStats && (<Card
                                                sx={{
                                                    borderRadius: 2.5,
                                                    boxShadow: 'none',
                                                    border: `1px solid ${COLORS.neutral[200]}`,
                                                    height: '100%',
                                                }}
                                            >
                                                <CardContent>
                                                    <Stack spacing={0.75}>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            fontWeight={700}
                                                            noWrap
                                                            title={stats?.shuntingStats?.company_name || 'N/A'}
                                                        >
                                                            Paid {stats.shuntingStats?.company_name || 'Shunting'}
                                                        </Typography>

                                                        <Typography variant="h6" fontWeight={800} color={'green'}>
                                                            {formatCurrency(stats?.shuntingStats?.paidAmount || 0)}
                                                        </Typography>

                                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                                            {formatNumber(stats?.shuntingStats?.paidJobs || 0)} Jobs
                                                        </Typography>
                                                    </Stack>
                                                </CardContent>
                                            </Card>)}
                                            {stats?.shuntingStats && (<Card
                                                sx={{
                                                    borderRadius: 2.5,
                                                    boxShadow: 'none',
                                                    border: `1px solid ${COLORS.neutral[200]}`,
                                                    height: '100%',
                                                }}
                                            >
                                                <CardContent>
                                                    <Stack spacing={0.75}>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            fontWeight={700}
                                                            noWrap
                                                            title={stats?.shuntingStats?.company_name || 'N/A'}
                                                        >
                                                            Unpaid {stats.shuntingStats?.company_name || 'Shunting'}
                                                        </Typography>

                                                        <Typography variant="h6" fontWeight={800} color={'red'}>
                                                            {formatCurrency(stats?.shuntingStats?.unpaidAmount || 0)}
                                                        </Typography>

                                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                                            {formatNumber(stats?.shuntingStats?.unpaidJobs || 0)} Jobs
                                                        </Typography>
                                                    </Stack>
                                                </CardContent>
                                            </Card>)}
                                            {!trailerSpend.length ? (
                                                <Grid size={{ xs: 12 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        No trailer spending data available.
                                                    </Typography>
                                                </Grid>
                                            ) : null}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, lg: 6 }}>
                                <Card sx={{ borderRadius: 3, height: 430 }}>
                                    <CardContent sx={{ height: '100%' }}>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            Top Suppliers by Spend ({selectedYear})
                                        </Typography>

                                        <Box sx={{ width: '100%', height: 350 }}>
                                            <ResponsiveContainer>
                                                <BarChart
                                                    data={topSuppliersChartData}
                                                    layout="vertical"
                                                    margin={{ left: 120, right: 20 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis
                                                        type="number"
                                                        tickFormatter={(value) => formatCompactCurrency(Number(value || 0))}
                                                    />
                                                    <YAxis type="category" dataKey="name" width={160} />
                                                    <Tooltip formatter={(value) => formatCurrency(Number(value ?? 0))} />
                                                    <Legend />
                                                    <Bar
                                                        dataKey="totalAmount"
                                                        name="Amount"
                                                        fill={COLORS.brand.primary}
                                                        radius={[0, 8, 8, 0]}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={{ xs: 12, lg: 6 }}>
                                <Card sx={{ borderRadius: 3, height: 430 }}>
                                    <CardContent sx={{ height: '100%' }}>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            Top Receiving Locations by Spend ({selectedYear})
                                        </Typography>

                                        <Box sx={{ width: '100%', height: 350 }}>
                                            <ResponsiveContainer>
                                                <BarChart
                                                    data={topLocationsChartData}
                                                    layout="vertical"
                                                    margin={{ left: 120, right: 20 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis
                                                        type="number"
                                                        tickFormatter={(value) => formatCompactCurrency(Number(value || 0))}
                                                    />
                                                    <YAxis type="category" dataKey="name" width={160} />
                                                    <Tooltip formatter={(value) => formatCurrency(Number(value ?? 0))} />
                                                    <Legend />
                                                    <Bar
                                                        dataKey="totalAmount"
                                                        name="Amount"
                                                        fill={COLORS.status.info}
                                                        radius={[0, 8, 8, 0]}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, lg: 4 }}>
                                <SpendTable
                                    title="Supplier Details"
                                    nameLabel="Supplier"
                                    rows={topSuppliersBySpend}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, lg: 4 }}>
                                <SpendTable
                                    title="Receiving Location Details"
                                    nameLabel="Location"
                                    rows={topReceivingLocationsBySpend}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, lg: 4 }}>
                                <TrailerSpendTable rows={trailerSpend} />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <InfoCard
                                    title="Monthly Amount Total"
                                    value={formatCurrency(totalMonthlyAmount)}
                                    subtitle={`${previousYear} and ${selectedYear} combined`}
                                    color="primary"
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <InfoCard
                                    title="Monthly BOL Total"
                                    value={formatNumber(totalMonthlyBols)}
                                    subtitle="BOL count from monthly comparison"
                                    color="info"
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <InfoCard
                                    title="Monthly Skid Total"
                                    value={formatNumber(totalMonthlySkids)}
                                    subtitle="Skids from monthly comparison"
                                    color="secondary"
                                />
                            </Grid>
                        </Grid>
                    </>
                )}
            </Stack>
        </Box>
    );
}

function StatsSummaryCard({
    title,
    count,
    amount,
    skids,
    status,
}: {
    title: string;
    count: number;
    amount: number;
    skids: number;
    status: string;
}) {
    const color = colorsMap[status] || COLORS.brand.primary;

    return (
        <Card
            sx={{
                borderRadius: 3,
                height: '100%',
                border: `1px solid ${COLORS.neutral[200]}`,
                boxShadow: 'none',
            }}
        >
            <CardContent>
                <Stack spacing={1}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: COLORS.neutral[500],
                            fontWeight: 700,
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        variant="h5"
                        fontWeight={800}
                        color={color}
                        sx={{
                            lineHeight: 1.15,
                            wordBreak: 'break-word',
                        }}
                    >
                        {formatCurrency(amount)}
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            color: COLORS.neutral[400],
                            fontWeight: 600,
                        }}
                    >
                        {formatNumber(count)} BOLs
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            color: COLORS.neutral[400],
                            fontWeight: 600,
                        }}
                    >
                        {formatNumber(skids)} Skids
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
}

function InfoCard({
    title,
    value,
    subtitle,
    color,
}: {
    title: string;
    value: string;
    subtitle: string;
    color: 'success' | 'error' | 'warning' | 'info' | 'primary' | 'secondary';
}) {
    return (
        <Card
            sx={{
                borderRadius: 3,
                height: '100%',
                border: `1px solid ${COLORS.neutral[200]}`,
                boxShadow: 'none',
            }}
        >
            <CardContent>
                <Stack spacing={0.75}>
                    <Typography variant="body2" color="text.secondary" fontWeight={700}>
                        {title}
                    </Typography>
                    <Typography variant="h5" fontWeight={800} color={colorsMap[color]}>
                        {value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {subtitle}
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
}

function SpendTable({
    title,
    nameLabel,
    rows,
}: {
    title: string;
    nameLabel: string;
    rows: TopSpendStat[];
}) {
    return (
        <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                    {title}
                </Typography>

                <Stack spacing={1.1}>
                    <Row label={nameLabel} value="Amount / BOLs / Skids" color="secondary" />

                    {rows.map((item) => (
                        <Row
                            key={item.name}
                            label={item.name}
                            value={`${formatCurrency(item.totalAmount)} / ${formatNumber(item.totalBols)} / ${formatNumber(item.totalSkids)}`}
                            color="primary"
                        />
                    ))}

                    {!rows.length ? (
                        <Typography variant="body2" color="text.secondary">
                            No data available.
                        </Typography>
                    ) : null}
                </Stack>
            </CardContent>
        </Card>
    );
}

function TrailerSpendTable({ rows }: { rows: TrailerSpendStat[] }) {
    return (
        <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                    Trailer Spend Details
                </Typography>

                <Stack spacing={1.1}>
                    <Row label="Trailer" value="Amount / BOLs / Skids" color="secondary" />

                    {rows.map((item) => (
                        <Row
                            key={item.trailer}
                            label={item.trailer}
                            value={`${formatCurrency(item.totalAmount)} / ${formatNumber(item.totalBols)} / ${formatNumber(item.totalSkids)}`}
                            color="primary"
                        />
                    ))}

                    {!rows.length ? (
                        <Typography variant="body2" color="text.secondary">
                            No data available.
                        </Typography>
                    ) : null}
                </Stack>
            </CardContent>
        </Card>
    );
}

const colorsMap: Record<string, string> = {
    success: COLORS.status.success,
    error: COLORS.status.error,
    warning: COLORS.status.warning,
    info: COLORS.status.info,
    primary: COLORS.brand.primary,
    secondary: COLORS.brand.dark,
};

function Row({
    label,
    value,
    color,
}: {
    label: string;
    value: React.ReactNode;
    color?: 'success' | 'error' | 'warning' | 'info' | 'primary' | 'secondary';
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Typography
                variant="body2"
                color={COLORS.neutral[500]}
                sx={{
                    maxWidth: '55%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
                title={String(label)}
            >
                {label}
            </Typography>

            <Typography
                variant="body2"
                fontWeight={700}
                color={colorsMap[color || 'primary']}
                textAlign="right"
            >
                {value}
            </Typography>
        </Box>
    );
}
